import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { STORY_DATA } from '../context/story/StoryData';
import { getLanguageFromPath, localizedPath, localizedStoryPath } from '../utils/routes';
import { findStoryChapterBySlug, getStoryRouteSlug } from '../utils/storyRoutes';

import {
    ChevronLeft, MapPin, Target, LayoutList, Menu,
    CheckSquare, Square, ZoomIn, Quote, AlertTriangle, Key,
    ChevronRight, FolderOpen
} from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

function StoryGuide() {
    const { t, language } = useLanguage();
    const { chapterId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const routeLanguage = getLanguageFromPath(location.pathname);
    const activeLanguage = routeLanguage || language;

    // STATE
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [activeStep, setActiveStep] = useState(null);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    // TAKİP SİSTEMİ (LocalStorage)
    const [completedSteps, setCompletedSteps] = useState(() => {
        try {
            const saved = localStorage.getItem('story_guide_progress');
            return saved ? JSON.parse(saved) : {};
        } catch (e) { return {}; }
    });

    useEffect(() => {
        localStorage.setItem('story_guide_progress', JSON.stringify(completedSteps));
    }, [completedSteps]);

    // URL'den chapter seçimi
    useEffect(() => {
        if (chapterId) {
            const chapter = findStoryChapterBySlug(STORY_DATA, chapterId);
            if (chapter) {
                setSelectedChapter(chapter);
            } else {
                navigate(localizedPath('story', activeLanguage), { replace: true });
            }
        } else {
            setSelectedChapter(null);
        }
    }, [activeLanguage, chapterId, navigate]);

    // Bölüm seçilince otomatik olarak "Yapılmamış İlk Görevi" bul
    useEffect(() => {
        if (selectedChapter && selectedChapter.questMap) {
            const firstIncomplete = selectedChapter.questMap.find(step => !completedSteps[`main_${selectedChapter.id}_${step.id}`]);
            if (firstIncomplete) {
                setActiveStep(firstIncomplete);
            } else if (selectedChapter.questMap.length > 0) {
                setActiveStep(selectedChapter.questMap[selectedChapter.questMap.length - 1]);
            }
        }
    }, [selectedChapter, completedSteps]);

    // Helper: Çeviri
    const getText = (textObj) => {
        if (!textObj) return "";
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || "Translation missing";
    };

    const getGuideImageAlt = (guide, imageIndex) => {
        const chapterName = selectedChapter ? t(selectedChapter.titleKey) : 'Tarkov story guide';
        const stepName = getText(activeStep?.label);
        const guideText = getText(guide?.text);
        return `${chapterName} - ${stepName} visual ${imageIndex + 1}: ${guideText}`;
    };

    // Helper: İlerleme
    const calculateProgress = (chapter) => {
        if (!chapter.questMap || chapter.questMap.length === 0) return 0;
        const total = chapter.questMap.length;
        const done = chapter.questMap.filter(step => completedSteps[`main_${chapter.id}_${step.id}`]).length;
        return Math.round((done / total) * 100);
    };

    const toggleComplete = (id) => {
        setCompletedSteps(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // --- RENDER: DASHBOARD ---
    if (!selectedChapter) {
        return (
            <div className="h-[calc(100vh-64px)] w-full bg-[#050505] text-white overflow-y-auto custom-scrollbar p-8 relative">
                <div className="absolute inset-0 pointer-events-none opacity-[0.03]"
                    style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }}>
                </div>

                <div className="max-w-6xl mx-auto relative z-10">
                    <div className="mb-12 text-center">
                        <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter text-white">
                            {t('story_chapters')}
                        </h1>
                        <p className="text-zinc-500 max-w-2xl mx-auto text-lg">
                            Select an operation file to begin tracking your progress.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {STORY_DATA.map((chapter) => {
                            const progress = calculateProgress(chapter);
                            const isComplete = progress === 100;

                            return (
                                <button
                                    key={chapter.id}
                                    onClick={() => navigate(localizedStoryPath(getStoryRouteSlug(chapter), activeLanguage))}
                                    className="group relative bg-[#0a0a0a] border border-zinc-800 hover:border-emerald-500/50 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-900/10 text-left h-64 flex flex-col"
                                >
                                    <div className="h-24 bg-zinc-900/50 border-b border-zinc-800 group-hover:bg-zinc-800 transition-colors relative overflow-hidden">
                                        {chapter.heroImage && (
                                            <LazyLoadImage
                                                src={chapter.heroImage}
                                                alt={`${t(chapter.titleKey)} Escape from Tarkov story chapter preview`}
                                                effect="blur"
                                                className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-45 transition-opacity"
                                            />
                                        )}
                                        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
                                        <div className="absolute bottom-4 left-4">
                                            <span className="text-5xl font-black text-white/5 uppercase tracking-tighter group-hover:text-emerald-500/10 transition-colors">
                                                {chapter.shortName}
                                            </span>
                                        </div>
                                        
                                        <div className="absolute top-4 right-4">
                                            {isComplete ? (
                                                <div className="bg-emerald-500 text-black text-[10px] font-bold px-2 py-1 rounded uppercase flex items-center gap-1">
                                                    <CheckSquare className="w-3 h-3" /> {t('story_chapter_completed')}
                                                </div>
                                            ) : (
                                                <div className="bg-zinc-800 text-zinc-400 text-[10px] font-bold px-2 py-1 rounded uppercase flex items-center gap-1 border border-zinc-700">
                                                    {progress}% {t('story_progress_done')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                                                {t(chapter.titleKey)}
                                            </h3>
                                            <p className="text-sm text-zinc-500 line-clamp-2">
                                                {chapter.description ? t(chapter.description) : "Classified operation details..."}
                                            </p>
                                        </div>
                                        <div className="mt-4">
                                            <div className="h-1.5 w-full bg-zinc-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER: MAIN GUIDE ---
    return (
        <div className="h-[calc(100vh-64px)] w-full bg-[#050505] text-white flex overflow-hidden font-sans transition-all duration-300 relative selection:bg-emerald-500/30">

            {previewImage && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200" onClick={() => setPreviewImage(null)}>
                    <LazyLoadImage src={previewImage} alt="Preview" effect="blur" className="max-w-full max-h-full object-contain rounded border border-zinc-800 shadow-2xl" />
                </div>
            )}

            {/* LEFT SIDEBAR */}
            <div className={`border-r border-zinc-800 flex flex-col bg-black transition-all duration-300 ${isSidebarCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'w-[400px] min-w-[320px]'} relative z-20`}>
                <div className="h-16 border-b border-zinc-800 flex items-center justify-between px-4 bg-zinc-900/30 shrink-0">
                    <button onClick={() => navigate(localizedPath('story', activeLanguage))} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest">
                        <ChevronLeft className="w-4 h-4" /> {t('story_chapters')}
                    </button>
                    <div className="text-emerald-500 text-[10px] font-black bg-emerald-900/20 px-2 py-1 rounded border border-emerald-900/30">
                        {Math.round(calculateProgress(selectedChapter))}% COMPLETED
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-0 bg-[#080808]">
                    {selectedChapter.questMap.map((step, index) => {
                        const isActive = activeStep?.id === step.id;
                        const isCompleted = completedSteps[`main_${selectedChapter.id}_${step.id}`];
                        const hasWarning = step.details?.warning;
                        const isLast = index === selectedChapter.questMap.length - 1;

                        return (
                            <div key={step.id} className="relative group">
                                {!isLast && (
                                    <div className={`absolute left-[23px] top-10 bottom-[-20px] w-[2px] z-0 transition-colors duration-300 ${isCompleted ? 'bg-emerald-500/20' : 'bg-zinc-800'}`}></div>
                                )}
                                <button
                                    onClick={() => setActiveStep(step)}
                                    className={`w-full text-left p-4 pr-12 relative transition-all border-b border-zinc-900/50 flex gap-4 ${isActive ? 'bg-zinc-900' : 'hover:bg-zinc-900/40'}`}
                                >
                                    {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500"></div>}
                                    <div className={`relative z-10 w-6 h-6 shrink-0 rounded flex items-center justify-center border text-[10px] font-bold transition-all mt-1 ${isCompleted ? 'bg-emerald-500 border-emerald-500 text-black' : isActive ? 'bg-white border-white text-black' : hasWarning ? 'bg-amber-900/20 border-amber-500/50 text-amber-500' : 'bg-black border-zinc-700 text-zinc-500'}`}>
                                        {isCompleted ? <CheckSquare className="w-3.5 h-3.5" /> : (hasWarning ? <AlertTriangle className="w-3.5 h-3.5" /> : (index + 1))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-[10px] font-mono uppercase tracking-widest ${isActive ? 'text-emerald-400' : 'text-zinc-600'}`}>
                                                {step.type === 'start' ? t('story_step_initial') : `${t('story_step_prefix')} ${index + 1}`}
                                            </span>
                                        </div>
                                        <h4 className={`text-sm font-bold leading-tight truncate transition-colors ${isCompleted ? 'text-zinc-500 line-through' : (isActive ? 'text-white' : 'text-zinc-400')}`}>
                                            {getText(step.label)}
                                        </h4>
                                    </div>
                                    {isActive && <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* RIGHT MAIN CONTENT */}
            <div className="flex-1 bg-[#0e0e10] flex flex-col relative h-full w-full">
                <button onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} className="absolute left-4 top-4 z-50 p-2 bg-black border border-zinc-800 rounded hover:border-zinc-600 text-zinc-400 hover:text-white transition-all shadow-xl">
                    <Menu className="w-5 h-5" />
                </button>

                {activeStep ? (
                    <div className="relative z-10 h-full flex flex-col overflow-y-auto custom-scrollbar">
                        {/* HEADER */}
                        <div className="p-8 pb-8 pt-16 border-b border-zinc-800 bg-gradient-to-b from-[#050505] to-[#0e0e10] sticky top-0 z-30 shadow-2xl">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border bg-zinc-800 text-zinc-400 border-zinc-700">
                                            {t('story_directive_label')}
                                        </span>
                                        {activeStep.details?.location && (
                                            <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border bg-zinc-900 text-zinc-500 border-zinc-800">
                                                <MapPin className="w-3 h-3" /> {getText(activeStep.details.location)}
                                            </span>
                                        )}
                                    </div>
                                    <h2 className="text-2xl md:text-4xl font-black text-white leading-tight tracking-tight font-sans max-w-3xl">
                                        {getText(activeStep.label)}
                                    </h2>
                                </div>
                                <button
                                    onClick={() => toggleComplete(`main_${selectedChapter.id}_${activeStep.id}`)}
                                    className={`group px-8 py-4 rounded-lg font-bold uppercase tracking-wider text-sm transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center gap-3 border-2 ${completedSteps[`main_${selectedChapter.id}_${activeStep.id}`] ? 'bg-emerald-500 text-black border-emerald-500 hover:bg-emerald-600' : 'bg-gradient-to-r from-zinc-800 to-zinc-900 text-white border-zinc-600 hover:border-emerald-400 hover:bg-emerald-500/10 hover:shadow-emerald-500/20'}`}
                                >
                                    {completedSteps[`main_${selectedChapter.id}_${activeStep.id}`] ? <><CheckSquare className="w-6 h-6" /> {t('story_completed')}</> : <><Square className="w-6 h-6" /> {t('story_mark_complete')}</>}
                                </button>
                            </div>
                        </div>

                        <div className="p-8 md:p-12 space-y-10 max-w-6xl mx-auto w-full pb-32">
                            {selectedChapter.heroImage && (
                                <figure className="overflow-hidden rounded-lg border border-zinc-800 bg-black shadow-md">
                                    <button type="button" className="block w-full cursor-zoom-in" onClick={() => setPreviewImage(selectedChapter.heroImage)}>
                                        <LazyLoadImage
                                            src={selectedChapter.heroImage}
                                            alt={`${t(selectedChapter.titleKey)} Escape from Tarkov story route overview`}
                                            effect="blur"
                                            className="max-h-[420px] w-full object-cover opacity-90 hover:opacity-100 transition-opacity"
                                        />
                                    </button>
                                    <figcaption className="border-t border-zinc-800 px-5 py-3 text-xs text-zinc-500">
                                        {t(selectedChapter.titleKey)} route overview for Escape from Tarkov story progression.
                                    </figcaption>
                                </figure>
                            )}

                            {/* 1. OBJECTIVE & LORE & INTEL */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Sol: Objective */}
                                <div className="lg:col-span-2 bg-zinc-900/20 border border-zinc-800 rounded-xl p-6 relative overflow-hidden group hover:border-zinc-700 transition-colors h-fit">
                                    <div className="absolute top-0 left-0 bottom-0 w-1 bg-emerald-500"></div>
                                    <h4 className="text-emerald-500 text-[11px] font-black uppercase mb-3 tracking-widest flex items-center gap-2">
                                        <Target className="w-4 h-4" /> {t('story_objective_label')}
                                    </h4>
                                    <p className="text-xl md:text-2xl text-emerald-100 font-medium font-mono leading-relaxed">
                                        "{getText(activeStep.details?.objective)}"
                                    </p>
                                </div>

                                {/* Sağ: Lore ve Intel Stack */}
                                <div className="flex flex-col gap-4">
                                    {/* Hikaye Özeti */}
                                    {activeStep.details?.story && (
                                        <div className="bg-black border border-zinc-800 rounded-xl p-6 relative">
                                            <Quote className="absolute right-4 top-4 w-8 h-8 text-zinc-800 opacity-50" />
                                            <h4 className="text-zinc-500 text-[11px] font-black uppercase mb-3 tracking-widest flex items-center gap-2">
                                                <FolderOpen className="w-4 h-4" /> {t('story_lore_label')}
                                            </h4>
                                            <p className="text-zinc-400 text-sm italic leading-relaxed font-serif">
                                                {getText(activeStep.details.story)}
                                            </p>
                                        </div>
                                    )}

                                    {/* YENİ INTEL KART (PDF Verileri) */}
                                    {activeStep.details?.intel && (
                                        <div className="bg-[#151517] border border-zinc-800 rounded-xl overflow-hidden shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
                                            <div className="bg-zinc-900/50 p-4 border-b border-zinc-800 flex gap-4 items-start">
                                                {activeStep.details.intel.itemImage && (
                                                    <div className="w-16 h-16 shrink-0 bg-black border border-zinc-700 rounded flex items-center justify-center overflow-hidden">
                                                        <LazyLoadImage src={activeStep.details.intel.itemImage} alt="Intel" effect="blur" className="w-full h-full object-cover opacity-90 hover:scale-110 transition-transform" />
                                                    </div>
                                                )}
                                                <div>
                                                    <h4 className="text-emerald-500 text-[10px] font-black uppercase mb-1 tracking-widest">
                                                        INTELLIGENCE FOUND
                                                    </h4>
                                                    <h3 className="text-white text-sm font-bold leading-tight">
                                                        {getText(activeStep.details.intel.itemName)}
                                                    </h3>
                                                    <p className="text-zinc-500 text-[10px] mt-1">
                                                        {getText(activeStep.details.intel.description)}
                                                    </p>
                                                </div>
                                            </div>
                                            {activeStep.details.intel.transcript && (
                                                <div className="p-4 bg-[#0a0a0a]">
                                                    <div className="text-zinc-400 text-xs font-mono leading-relaxed whitespace-pre-line p-3 border-l-2 border-zinc-800 bg-zinc-900/20">
                                                        {getText(activeStep.details.intel.transcript)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. WARNINGS & ITEMS */}
                            {(activeStep.details?.warning || activeStep.details?.requiredItems) && (
                                <div className="space-y-4">
                                    {activeStep.details.warning && (
                                        <div className="border border-amber-500/20 bg-amber-500/5 rounded-xl p-5 flex gap-5 items-start">
                                            <div className="bg-amber-500/10 p-2 rounded-lg shrink-0"><AlertTriangle className="w-6 h-6 text-amber-500" /></div>
                                            <div>
                                                <h4 className="text-amber-500 text-xs font-black uppercase mb-1 tracking-widest">{t('story_bug_warning')}</h4>
                                                <p className="text-amber-100/80 text-sm font-medium leading-relaxed">{getText(activeStep.details.warning)}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activeStep.details.requiredItems && (
                                        <div className="border border-blue-500/20 bg-blue-500/5 rounded-xl p-5 flex gap-5 items-start">
                                            <div className="bg-blue-500/10 p-2 rounded-lg shrink-0"><Key className="w-6 h-6 text-blue-400" /></div>
                                            <div>
                                                <h4 className="text-blue-400 text-xs font-black uppercase mb-2 tracking-widest">{t('story_req_label')}</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {activeStep.details.requiredItems.map((item, i) => (
                                                        <span key={i} className="text-xs font-mono font-bold bg-blue-900/30 text-blue-200 px-3 py-1.5 rounded border border-blue-500/20 shadow-sm">{getText(item)}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 2.5. REWARDS */}
                            {activeStep.details.rewards && (
    <div className="mt-6 bg-gradient-to-r from-cyan-900/20 to-transparent border-l-4 border-cyan-500 rounded-r-lg p-5 shadow-[inset_0px_0px_20px_rgba(6,182,212,0.05)]">
        <h4 className="text-cyan-500 font-black text-sm uppercase tracking-widest mb-2 flex items-center gap-2 drop-shadow-[0_0_8px_rgba(6,182,212,0.5)]">
            <Target className="w-4 h-4" /> {t('rewards') || 'REWARDS'}
        </h4>
        <p className="text-cyan-100/90 text-sm font-medium">
            {getText(activeStep.details.rewards)}
        </p>
    </div>
)}

                            {/* 3. EXECUTION STEPS */}
                            <div className="pt-8 border-t border-zinc-900">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="bg-zinc-800 p-2 rounded text-white shadow-lg"><LayoutList className="w-5 h-5" /></div>
                                    <h4 className="text-white text-base font-black uppercase tracking-widest">{t('story_execution_label')}</h4>
                                </div>
                                <div className="space-y-12">
                                    {activeStep.details?.guideSteps && activeStep.details.guideSteps.map((guide, idx) => {
                                        const isDone = completedSteps[`guide_${selectedChapter.id}_${activeStep.id}_${idx}`];
                                        return (
                                            <div key={idx} className={`relative transition-all duration-300 ${isDone ? 'opacity-60 grayscale-[0.5]' : ''}`}>
                                                <div className="absolute left-[-29px] top-8 bottom-0 w-[2px] bg-zinc-800 lg:block hidden"></div>
                                                <div className="bg-[#121214] border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-zinc-700 transition-colors shadow-sm">
                                                    <div className="flex items-center gap-4 p-4 border-b border-zinc-800/50 bg-black/20">
                                                        <button className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center border transition-all text-xs font-bold ${isDone ? 'bg-emerald-500 border-emerald-500 text-black' : 'bg-black border-zinc-700 text-zinc-400 hover:border-white hover:text-white'}`} onClick={() => toggleComplete(`guide_${selectedChapter.id}_${activeStep.id}_${idx}`)}>
                                                            {isDone ? <CheckSquare className="w-4 h-4" /> : (idx + 1)}
                                                        </button>
                                                        <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">{t('story_action_step')} {idx + 1}</span>
                                                    </div>
                                                    <div className="p-6">
                                                        <p className={`text-base md:text-lg leading-relaxed text-zinc-300 mb-6 font-medium ${isDone ? 'line-through decoration-zinc-600' : ''}`}>{getText(guide.text)}</p>
                                                        {guide.images && guide.images.length > 0 && (
                                                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                {guide.images.map((imgUrl, imgIdx) => (
                                                                    <figure key={imgUrl} className="group relative bg-black rounded-lg border border-zinc-800 overflow-hidden shadow-md">
                                                                        <button type="button" className="relative block aspect-video w-full cursor-zoom-in" onClick={() => setPreviewImage(imgUrl)}>
                                                                            <LazyLoadImage src={imgUrl} alt={getGuideImageAlt(guide, imgIdx)} effect="blur" className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity duration-300" />
                                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                <div className="absolute bottom-3 right-3 bg-emerald-500 text-black px-2 py-1 rounded text-[10px] font-bold uppercase flex items-center gap-1"><ZoomIn className="w-3 h-3" /> {t('story_zoom')}</div>
                                                                            </div>
                                                                        </button>
                                                                        <figcaption className="border-t border-zinc-800 px-3 py-2 text-[11px] leading-relaxed text-zinc-500">
                                                                            {t('story_action_step')} {idx + 1} - {getText(activeStep.label)}
                                                                        </figcaption>
                                                                    </figure>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (<div className="flex-1"></div>)}
            </div>
        </div>
    );
}

export default StoryGuide;
